import { faMagic } from "@fortawesome/duotone-light-svg-icons"
import {
  Button,
  JoonUIColor,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import { useState } from "react"

import useSessionModalStore from "./hooks/useSessionModalStore"
import LeavesLoader from "../../../components/loader/LeavesLoader"
import { notesPlaceholderText } from "../../../constants"
import useUserRole from "../../../hooks/useUserRole"
import { queryClient } from "../../../queryClient"
import { QueryKey } from "../../../types/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { createJoonAPIClient } from "../../../util/joon-api"

const GenerateNotesSection = ({
  sessionBookingId,
  setSummaryNotes,
}: {
  sessionBookingId: number
  setSummaryNotes: (notes: string) => void
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const { isClientOfOtherCoach } = useUserRole()
  const { setIsWritingManualNotes } = useSessionModalStore()

  const handleGenerateNotes = async () => {
    if (isLoading) return
    setIsLoading(true)
    setShowError(false)
    try {
      const API = createJoonAPIClient()
      await API.post(
        `/api/session-bookings/${sessionBookingId}/generate-summary/`
      )
      await queryClient.invalidateQueries([QueryKey.SESSION_BOOKING])
    } catch (error) {
      setShowError(true)
    }
    setIsLoading(false)
  }

  const handleWriteManualNotes = () => {
    setIsWritingManualNotes(true)
    setShowError(false)
    setSummaryNotes(notesPlaceholderText)
    trackAnalyticEvent(ANALYTIC_EVENTS.POST_SESSION_FLOW_GENERATE_NOTE, {
      generated_notes: false,
    })
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 justify-center items-center">
        <LeavesLoader />
        <div className="flex flex-col items-center">
          <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
            Generating AI summary if session was recorded
          </Typography>
          <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
            This may take a few minutes
          </Typography>
        </div>
      </div>
    )
  }

  if (showError) {
    return (
      <div className="flex flex-col gap-4 justify-center items-center max-w-[400px] text-center">
        <img src="/images/squelchee_sad.png" alt="error" />
        <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
          We're unable to generate AI Summary right now.{" "}
        </Typography>
        <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
          If you did record this session and run into this issue, please try
          again in 15 minutes. If that still does not work, please reach out to
          our team and write manual notes.
        </Typography>
        <Button
          text="Try again"
          onClick={() => {
            handleGenerateNotes()
            trackAnalyticEvent(
              ANALYTIC_EVENTS.POST_SESSION_FLOW_TRY_AGAIN_AI_NOTE
            )
          }}
        />
        <TextButton onClick={handleWriteManualNotes}>
          <Typography variant="body" color={JoonUIColor.semantic.primary}>
            Write manual notes instead
          </Typography>
        </TextButton>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2 justify-center items-center">
      <Typography variant="bodySmall">
        If this session was recorded, you can:
      </Typography>
      <Button
        buttonType="secondary"
        text="Generate AI summary"
        icon={isLoading ? undefined : faMagic}
        onClick={() => {
          trackAnalyticEvent(ANALYTIC_EVENTS.POST_SESSION_FLOW_GENERATE_NOTE, {
            generated_notes: true,
          })
          handleGenerateNotes()
        }}
        disabled={isLoading || isClientOfOtherCoach}
      />
      <button onClick={handleWriteManualNotes}>
        <Typography variant="bodySmall" color={JoonUIColor.semantic.primary}>
          Session wasn't recorded
        </Typography>
      </button>
    </div>
  )
}

export default GenerateNotesSection
